import { Newspaper, Share2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  DASHBOARD_NULL_KEY,
  type DashboardMetric,
  type SocialReportDashboard,
  type SnapshotFacetFilters
} from "@/shared/api/api-client";
import { BrazilMap } from "@/shared/components/charts/BrazilMap";
import { categoricalTone, ChartToggle } from "@/shared/components/charts/chart-kit";
import { DonutChart } from "@/shared/components/charts/DonutChart";
import { EmptyState } from "@/shared/components/charts/EmptyState";
import { IntensityHeatmap } from "@/shared/components/charts/IntensityHeatmap";
import { MultiLineChart } from "@/shared/components/charts/MultiLineChart";
import { Panel } from "@/shared/components/charts/Panel";
import { PolarityColumns } from "@/shared/components/charts/PolarityColumns";
import { RankedBars } from "@/shared/components/charts/RankedBars";
import { Select } from "@/shared/components/Select";
import { metricLabel } from "@/shared/filters/snapshot-facets";
import { polarityOptions, polarityPalette } from "@/shared/types/polarity";
import { formatCena, formatCompact, formatInteger } from "@/shared/utils/format";
import { formatCurrencyCompact } from "@/shared/utils/format-currency";

import { MEDIA_TYPE_LABELS, withoutNull, type SourceVisibility } from "./dashboard-v1";
import { MODE_OPTIONS, PolarityPanel, type PanelMode } from "./PolarityPanel";
import { StrategicMatrixPanel, type ReportReference } from "./StrategicMatrixPanel";

/** O que a foto antiga não tem — o aviso dos blocos que dependem da
 *  hora, do tipo de mídia ou da UF congelados (2026-10-25). */
export const NOT_IN_THIS_PHOTO =
  "Esta amostra foi gerado antes de a foto guardar hora, tipo de mídia e UF. Gere de novo (ou peça \"Atualizar dados\") pra ver.";

/** "Semana de 02/03" → "02/03": no eixo de colunas só cabe o dia. */
function shortLabel(label: string): string {
  return label.replace(/^Semana de /, "");
}

function sentimentSegments(items: DashboardMetric[], value: (item: DashboardMetric) => number) {
  const order = new Map(polarityOptions().map((option, index) => [option.name, index]));
  return [...items]
    .sort((a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99))
    .map(item => ({
      key: item.key,
      label: item.key === DASHBOARD_NULL_KEY ? "Sem classificação" : metricLabel(item),
      value: value(item),
      tone: polarityPalette(item.key === DASHBOARD_NULL_KEY ? null : item.key)
    }));
}

/** Share of Voice: as cinco marcas maiores com cor própria, o resto em
 *  "Outras" — a regra da paleta categórica (cinco cores, e a sexta em
 *  diante dobra num balde). */
function shareOfVoice(brands: DashboardMetric[]) {
  const sorted = [...brands].sort((a, b) => b.count - a.count);
  const head = sorted.slice(0, 5).map((item, index) => ({ key: item.key, label: metricLabel(item), value: item.count, tone: categoricalTone(index) }));
  const rest = sorted.slice(5).reduce((sum, item) => sum + item.count, 0);
  return rest ? [...head, { key: "__rest__", label: "Outras", value: rest, tone: "cat-other" }] : head;
}

type BandMetric = { label: string; value: string; sublabel: string };

/** Uma faixa "Visão geral" de fonte. Imprensa e Redes têm o mesmo
 *  desenho e o mesmo número de métricas (2026-10-30), pra lerem lado a
 *  lado. */
function SourceBand({ title, icon, metrics }: { title: string; icon: ReactNode; metrics: BandMetric[] }) {
  return (
    <section className="dashboard-press-panel dashboard-span-12" aria-label={title}>
      <div className="dashboard-press-header">
        <div className="dashboard-press-title">{icon}<span>{title}</span></div>
      </div>
      <div className="dashboard-press-grid">
        {metrics.map(metric => (
          <div key={metric.label}>
            <span className="dashboard-press-metric-label">{metric.label}</span>
            <strong className="dashboard-press-metric-value">{metric.value}</strong>
            <span className="dashboard-press-metric-sublabel">{metric.sublabel}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PressBand({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const press = data.sources.find(item => item.key === "press");
  return (
    <SourceBand
      title="Visão geral — Imprensa"
      icon={<Newspaper size={16}/>}
      metrics={[
        { label: "Volume total", value: formatInteger(press?.count ?? 0), sublabel: "Menções em imprensa" },
        { label: "Valoração", value: formatCurrencyCompact(data.total_ad_value), sublabel: data.total_ad_value === null ? "Nenhuma matéria valorada" : "Valor publicitário estimado" },
        { label: "Total de veículos", value: formatInteger(data.press_vehicle_count), sublabel: "Veículos distintos" },
        ...(showsCena ? [{ label: "Nota CENA", value: formatCena(press?.average_cena ?? null), sublabel: "Média da imprensa" }] : [])
      ]}
    />
  );
}

/** A interação e o alcance saem da fatia `network` de `sources`, não do
 *  total: imprensa não tem interação e o alcance dela é outro número
 *  (`press_reach`), mas a fatia diz o que é de rede sem depender disso. */
function NetworkBand({ data }: { data: SocialReportDashboard }) {
  const network = data.sources.find(item => item.key === "network");
  return (
    <SourceBand
      title="Visão geral — Redes"
      icon={<Share2 size={16}/>}
      metrics={[
        { label: "Volume total", value: formatInteger(network?.count ?? 0), sublabel: "Menções em redes" },
        { label: "Interações", value: formatCompact(network?.interactions ?? 0), sublabel: "Curtidas, comentários e compartilhamentos" },
        { label: "Alcance", value: formatCompact(network?.reach ?? 0), sublabel: "20% dos seguidores, cada perfil uma vez" },
        { label: "Perfis", value: formatInteger(data.network_profile_count), sublabel: "Perfis distintos que publicaram" }
      ]}
    />
  );
}

/** Uma série da linha do tempo, na largura do painel. */
function SingleLine({ data, tone, label, value, format }: {
  data: SocialReportDashboard;
  tone: string;
  label: string;
  value: (point: SocialReportDashboard["timeline"][number]) => number;
  format: (value: number) => string;
}) {
  return (
    <MultiLineChart
      labels={data.timeline.map(point => shortLabel(point.label))}
      series={[{ key: label, label, tone, values: data.timeline.map(value) }]}
      formatValue={format}
      formatAxis={format}
      area
    />
  );
}

function ChartPanel({ title, subtitle, className, controls, children }: {
  title: string; subtitle: string; className: string; controls?: ReactNode; children: ReactNode;
}) {
  return (
    <Panel title={title} subtitle={subtitle} className={className} action={controls} downloadable>
      {children}
    </Panel>
  );
}

function SentimentEvolution({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const [mode, setMode] = useState<PanelMode>("absolute");
  const points = data.timeline.map(point => ({ key: point.period, label: shortLabel(point.label), total: point.mentions, byPolarity: point.by_polarity }));
  return (
    <ChartPanel
      title="Evolução por Sentimento"
      subtitle="Volume de cada período empilhado por sentimento"
      className="dashboard-span-12"
      controls={<ChartToggle label="Escala" value={mode} options={MODE_OPTIONS} onChange={setMode}/>}
    >
      <PolarityColumns points={points} percent={mode === "percent"}/>
      {showsCena && (
        <div className="dashboard-subchart">
          <h3 className="dashboard-subchart-title">Nota CENA média no período</h3>
          <SingleLine data={data} tone="cat-1" label="CENA média" value={point => point.average_cena ?? 0} format={formatCena}/>
        </div>
      )}
    </ChartPanel>
  );
}

function PressDailyExposure({ data }: { data: SocialReportDashboard }) {
  const voices = [...new Set((data.spokesperson_timeline ?? []).map(point => point.key))];
  const [voice, setVoice] = useState("");
  const labels = data.timeline.map(point => shortLabel(point.label));
  const series = [{ key: "press", label: "Imprensa", tone: "cat-1", values: data.timeline.map(point => point.press_mentions) }];
  if (voice) {
    const byPeriod = new Map((data.spokesperson_timeline ?? []).filter(point => point.key === voice).map(point => [point.period, point.count]));
    series.push({ key: voice, label: voice, tone: "cat-2", values: data.timeline.map(point => byPeriod.get(point.period) ?? 0) });
  }
  return (
    <ChartPanel
      title="Exposição Diária (Imprensa)"
      subtitle="Menções de imprensa por período, com a linha do porta-voz escolhido"
      className="dashboard-span-12"
      controls={(
        <Select name="exposicao-porta-voz" aria-label="Porta-voz da linha" value={voice} onChange={event => setVoice(event.target.value)} disabled={!voices.length}>
          <option value="">{voices.length ? "Porta-voz (linha)" : "Sem porta-voz na imprensa"}</option>
          {voices.map(name => <option key={name} value={name}>{name}</option>)}
        </Select>
      )}
    >
      <MultiLineChart labels={labels} series={series}/>
    </ChartPanel>
  );
}

function TitleMention({ data }: { data: SocialReportDashboard }) {
  const [mode, setMode] = useState<PanelMode>("absolute");
  const base = data.title_mention_base;
  return (
    <>
      <ChartPanel title="Menção no Título" subtitle="Matérias impressas e online que citam a marca no título" className="dashboard-span-4">
        {base ? (
          <DonutChart
            title="Menção no título"
            centerValue={formatInteger(base)}
            centerLabel="matérias"
            segments={[
              { key: "in", label: "No título", value: data.title_mentions, tone: "cat-1" },
              { key: "out", label: "Fora do título", value: base - data.title_mentions, tone: "cat-other" }
            ]}
          />
        ) : <EmptyState label="Esta amostra foi gerado antes de a foto guardar a citação no título"/>}
      </ChartPanel>
      <ChartPanel
        title="Evolução de Menção no Título"
        subtitle={mode === "percent" ? "Percentual das matérias de imprensa do período" : "Matérias com a marca no título, por período"}
        className="dashboard-span-8"
        controls={<ChartToggle label="Escala" value={mode} options={MODE_OPTIONS} onChange={setMode}/>}
      >
        {base ? (
          <SingleLine
            data={data}
            tone="cat-1"
            label="No título"
            value={point => (mode === "percent" ? (point.press_mentions ? (point.title_mentions / point.press_mentions) * 100 : 0) : point.title_mentions)}
            format={value => (mode === "percent" ? `${Math.round(value)}%` : formatInteger(value))}
          />
        ) : <EmptyState label="Esta amostra foi gerado antes de a foto guardar a citação no título"/>}
      </ChartPanel>
    </>
  );
}

type CategoryKey = "influencer_types" | "sources" | "media_types" | "tiers" | "publisher_groups";

/** `only` = de que fonte a categoria é (porte é de rede, tier é de
 *  imprensa); "Fonte" só faz sentido com as duas na tela. */
const CATEGORIES: Array<{ value: CategoryKey; label: string; only: keyof SourceVisibility | "both" }> = [
  { value: "influencer_types", label: "Porte do publicador", only: "network" },
  { value: "publisher_groups", label: "Grupo de publicador", only: "network" },
  { value: "sources", label: "Fonte", only: "both" },
  { value: "media_types", label: "Tipo de mídia", only: "press" },
  { value: "tiers", label: "Tier do veículo", only: "press" }
];

function CategoryAnalysis({ data, showsCena, sources }: { data: SocialReportDashboard; showsCena: boolean; sources: SourceVisibility }) {
  const available = CATEGORIES.filter(option => (
    option.only === "both" ? sources.press && sources.network : sources[option.only]
  ));
  const [chosen, setChosen] = useState<CategoryKey>("influencer_types");
  // A escolha sobrevive à troca de fonte: volta a valer quando a fonte
  // dela volta pra tela.
  const category = available.some(option => option.value === chosen) ? chosen : available[0].value;
  const label = CATEGORIES.find(option => option.value === category)?.label ?? "";
  const items = category === "media_types" ? data.media_types : data[category];
  return (
    <PolarityPanel
      title={`Sentimento & Indicadores por ${label}`}
      subtitle="Volume por categoria e o indicador selecionado"
      items={items ?? []}
      kind={category === "media_types" ? "media" : "default"}
      showsCena={showsCena}
      highlights
      unavailable={items === null ? NOT_IN_THIS_PHOTO : undefined}
      extra={(
        <Select name="categoria" aria-label="Categoria" value={category} onChange={event => setChosen(event.target.value as CategoryKey)}>
          {available.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
      )}
    />
  );
}

type ThemeDimension = "themes" | "theme_groups" | "spokespersons";

function ThemeAnalysis({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const [dimension, setDimension] = useState<ThemeDimension>("themes");
  const options: Array<{ value: ThemeDimension; label: string }> = [
    { value: "theme_groups", label: "Tema" },
    { value: "themes", label: "Subtema" },
    ...(data.spokespersons !== null ? [{ value: "spokespersons" as const, label: "Porta-voz" }] : [])
  ];
  return (
    <PolarityPanel
      title="Sentimento & Indicadores por Tema e Subtema"
      subtitle="Uma menção em dois subtemas conta nos dois — as linhas somam mais que o total"
      items={withoutNull((dimension === "spokespersons" ? data.spokespersons : data[dimension]) ?? [])}
      kind="raw"
      showsCena={showsCena}
      highlights
      extra={(
        <Select name="dimensao-tema" aria-label="Dimensão" value={dimension} onChange={event => setDimension(event.target.value as ThemeDimension)}>
          {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
      )}
    />
  );
}

function OnlineProfiles({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const platforms = [...new Set(data.authors.map(item => item.parent).filter((value): value is string => Boolean(value)))];
  const [platform, setPlatform] = useState("");
  return (
    <PolarityPanel
      title="Polaridade por Perfil (Online)"
      subtitle="Os perfis que mais publicaram nas redes, por sentimento"
      items={platform ? data.authors.filter(item => item.parent === platform) : data.authors}
      kind="author"
      showsCena={showsCena}
      withIndicator={false}
      extra={(
        <Select name="rede" aria-label="Rede" value={platform} onChange={event => setPlatform(event.target.value)}>
          <option value="">Todas as redes</option>
          {platforms.map(name => <option key={name} value={name}>{name}</option>)}
        </Select>
      )}
    />
  );
}

function PublishersByMedia({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const [media, setMedia] = useState("");
  const pairs = data.press_channel_media;
  const items = media ? (pairs ?? []).filter(item => item.parent === media) : data.press_channels;
  return (
    <PolarityPanel
      title="Principais Publicadores por Tipo de Mídia"
      subtitle="Polaridade dos veículos que mais publicaram, filtrada pelo tipo de mídia"
      items={items}
      kind="raw"
      showsCena={showsCena}
      withIndicator={false}
      unavailable={media && pairs === null ? NOT_IN_THIS_PHOTO : undefined}
      extra={(
        <Select name="tipo-de-midia" aria-label="Tipo de mídia" value={media} onChange={event => setMedia(event.target.value)}>
          <option value="">Todos os tipos</option>
          {["print", "online", "radio", "tv"].map(key => <option key={key} value={key}>{MEDIA_TYPE_LABELS[key]}</option>)}
        </Select>
      )}
    />
  );
}

type VoiceMode = "count" | "percent" | "cena";

function Spokespersons({ data, showsCena }: { data: SocialReportDashboard; showsCena: boolean }) {
  const [mode, setMode] = useState<VoiceMode>("count");
  const items = withoutNull(data.spokespersons ?? []);
  const total = data.total_mentions;
  const shown = mode === "cena"
    ? [...items].filter(item => item.average_cena !== null).sort((a, b) => (b.average_cena ?? 0) - (a.average_cena ?? 0))
    : items;
  const options: Array<{ value: VoiceMode; label: string }> = [
    { value: "count", label: "123" },
    { value: "percent", label: "%" },
    ...(showsCena ? [{ value: "cena" as const, label: "CENA" }] : [])
  ];
  return (
    <Panel
      title="Porta-vozes"
      subtitle="Quem falou pela marca"
      className="dashboard-span-12"
      action={<ChartToggle label="Métrica" value={mode} options={options} onChange={setMode}/>}
      downloadable
    >
      {data.spokespersons === null ? <EmptyState label="Esta amostra foi gerado antes da dimensão Porta-voz"/> : (
        <RankedBars
          items={shown.map(item => ({ key: item.key, label: item.key, value: mode === "cena" ? item.average_cena ?? 0 : item.count }))}
          noun={mode === "cena" ? "de CENA média" : "menções"}
          limit={15}
          showShare={false}
          formatValue={value => (mode === "cena" ? formatCena(value) : mode === "percent" ? `${total ? Math.round((value / total) * 100) : 0}%` : formatInteger(value))}
        />
      )}
    </Panel>
  );
}

/**
 * A Visão Geral do dashboard do v1, na linguagem do v2. Os cartões do
 * topo continuam os da página (comuns a todas as abas).
 */
export function OverviewTab({ data, token, reference, filters, showsCena, sources }: {
  data: SocialReportDashboard;
  token: string;
  reference: ReportReference;
  filters: SnapshotFacetFilters;
  showsCena: boolean;
  /** Que fonte está na tela (2026-10-30): os blocos de uma fonte só
   *  somem quando ela não está. Interação e alcance da série são de
   *  rede; título, veículo, tipo de mídia e UF são de imprensa. */
  sources: SourceVisibility;
}) {
  const extendedNote = data.extended_dimensions ? undefined : NOT_IN_THIS_PHOTO;
  return (
    <div className="dashboard-section-grid">
      {sources.press && <PressBand data={data} showsCena={showsCena}/>}
      {sources.network && <NetworkBand data={data}/>}
      <SentimentEvolution data={data} showsCena={showsCena}/>
      {sources.network && (
        <>
          <ChartPanel title="Evolução de Interações" subtitle="Curtidas, comentários e compartilhamentos por período" className="dashboard-span-6">
            <SingleLine data={data} tone="cat-1" label="Interações" value={point => point.interactions} format={formatCompact}/>
          </ChartPanel>
          <ChartPanel title="Evolução de Alcance" subtitle="20% dos seguidores, cada perfil uma vez por período" className="dashboard-span-6">
            <SingleLine data={data} tone="cat-1" label="Alcance" value={point => point.reach} format={formatCompact}/>
          </ChartPanel>
        </>
      )}
      <ChartPanel title="Volume por Sentimento" subtitle="Distribuição das menções" className={sources.network ? "dashboard-span-6" : "dashboard-span-12"}>
        <DonutChart title="Volume por sentimento" centerValue={formatInteger(data.total_mentions)} centerLabel="menções" segments={sentimentSegments(data.sentiments, item => item.count)}/>
      </ChartPanel>
      {sources.network && (
        <ChartPanel title="Engajamento por Sentimento" subtitle="Soma das interações por polaridade" className="dashboard-span-6">
          <DonutChart title="Engajamento por sentimento" centerValue={formatCompact(data.total_interactions)} centerLabel="interações" formatValue={formatCompact} segments={sentimentSegments(data.sentiments, item => item.interactions)}/>
        </ChartPanel>
      )}
      <StrategicMatrixPanel token={token} reference={reference} filters={filters} data={data} showsCena={showsCena} sources={sources}/>
      <ChartPanel title="Share of Voice" subtitle="Participação de cada marca no volume" className="dashboard-span-4">
        <DonutChart title="Share of voice" centerValue={formatInteger(data.total_mentions)} centerLabel="menções" segments={shareOfVoice(data.brands)}/>
      </ChartPanel>
      <ChartPanel title="Mapa de Intensidade" subtitle="Concentração por dia da semana e hora da publicação" className="dashboard-span-8">
        {data.heatmap ? <IntensityHeatmap cells={data.heatmap}/> : <EmptyState label={NOT_IN_THIS_PHOTO}/>}
      </ChartPanel>
      {sources.network && <PolarityPanel title="Exposição & Indicadores (Redes)" subtitle="Volume por rede social, por sentimento, e o indicador selecionado" items={data.network_channels} kind="raw" showsCena={showsCena}/>}
      {sources.press && (
        <>
          <PolarityPanel title="Exposição & Indicadores (Imprensa)" subtitle="Volume por formato da matéria, por sentimento, e o indicador selecionado" items={withoutNull(data.formats)} kind="format" showsCena={showsCena}/>
          <PressDailyExposure data={data}/>
        </>
      )}
      <PolarityPanel title="Polaridade & Indicadores por Abordagem" subtitle="Volume por abordagem, por sentimento, e o indicador selecionado" items={data.approaches} showsCena={showsCena}/>
      <CategoryAnalysis data={data} showsCena={showsCena} sources={sources}/>
      <ThemeAnalysis data={data} showsCena={showsCena}/>
      {sources.press && <TitleMention data={data}/>}
      {sources.network && <OnlineProfiles data={data} showsCena={showsCena}/>}
      {sources.press && (
        <PolarityPanel
          title="Polaridade por Veículo (Impressos)"
          subtitle="Sentimento nos veículos de mídia impressa"
          items={(data.press_channel_media ?? []).filter(item => item.parent === "print")}
          kind="raw"
          showsCena={showsCena}
          withIndicator={false}
          unavailable={extendedNote}
        />
      )}
      <Spokespersons data={data} showsCena={showsCena}/>
      {sources.press && (
        <>
          <PublishersByMedia data={data} showsCena={showsCena}/>
          <ChartPanel title="Exposição por Estado (UF)" subtitle="Menções de imprensa pela UF do veículo" className="dashboard-span-12">
            {data.states ? <BrazilMap items={data.states}/> : <EmptyState label={NOT_IN_THIS_PHOTO}/>}
          </ChartPanel>
        </>
      )}
    </div>
  );
}
